import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import React, { useEffect } from 'react'
import CloseIcon from '@mui/icons-material/Close';
import { DateRangePicker, LocalizationProvider, MultiInputTimeRangeField, TimePicker } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

function DateTimeDialog(props) {
    const { open, onClose, title, editingOuterIndex, dateTimeConditions, selectedNode, setNodes } = props;
    const theme = useTheme();

    const [days, setDays] = React.useState([]);
    const [isWorkDay, setIsWorkDay] = React.useState(false);
    const [isWeekEnd, setIsWeekEnd] = React.useState(false);
    const [isEveryDay, setIsEveryDay] = React.useState(false);

    const [startTime, setStartTime] = React.useState(null);
    const [endTime, setEndTime] = React.useState(null);

    const [isConfirm, setIsConfirm] = React.useState(true);
    const [errorTime, setErrorTime] = React.useState(null);

    const [errorOverlap, setErrorOverlap] = React.useState(false);

    const workDay = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const weekEnd = ['saturday', 'sunday'];
    const everyDay = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    useEffect(() => {
        if (editingOuterIndex !== null && open) {
            setDays(dateTimeConditions[editingOuterIndex].days);
            setStartTime(dayjs(`2025-01-01T${dateTimeConditions[editingOuterIndex].start}`));
            setEndTime(dayjs(`2025-01-01T${dateTimeConditions[editingOuterIndex].end}`));
        } else {
            setDays([]);
            setStartTime(null);
            setEndTime(null);
            setErrorOverlap(false);
        }
    }, [editingOuterIndex, open]);

    const handleSelectedDay = (event, value) => {
        setDays(value);
    };

    useEffect(() => {
        const filterDayType = (days) => {
            const is_workday = workDay.every(day => days.includes(day)) && days.length === workDay.length;
            const is_weekend = weekEnd.every(day => days.includes(day)) && days.length === weekEnd.length;
            const is_everyday = everyDay.every(day => days.includes(day)) && days.length === everyDay.length;
            if (is_workday) {
                setIsWorkDay(true);
                setIsEveryDay(false);
                setIsWeekEnd(false);
            } else if (is_weekend) {
                setIsWeekEnd(true);
                setIsEveryDay(false);
                setIsWorkDay(false);
            } else if (is_everyday) {
                setIsEveryDay(true);
                setIsWorkDay(false);
                setIsWeekEnd(false);
            } else {
                setIsWorkDay(false);
                setIsWeekEnd(false);
                setIsEveryDay(false);
            }
        };
        if (days) {
            filterDayType(days);
        }
    }, [days])

    const handleSetDayTypes = (type) => {
        if (type === 'everyday' && !isEveryDay) {
            setDays(everyDay);
        } else if (type === 'workday' && !isWorkDay) {
            setDays(workDay);
        } else if (type === 'weekend' && !isWeekEnd) {
            setDays(weekEnd);
        } else {
            setIsEveryDay(false);
            setIsWorkDay(false);
            setIsWeekEnd(false);
            setDays([]);
        }
    };

    const handleTimeChange = (newValue, type) => {
        const timeStr = newValue.format('HH:mm');
        const timeDayjs = dayjs(`2025-01-01T${timeStr}`);
        if (type === 'start') {
            if (endTime && timeDayjs.isSameOrAfter(endTime)) {
                setErrorTime('Start time must be earlier than end time.');
                return;
            } else {
                setErrorTime(null);
            }
            setStartTime(timeDayjs);
        } else {
            if (startTime && timeDayjs.isSameOrBefore(startTime)) {
                setErrorTime('End time must be later than start time.');
                return;
            } else {
                setErrorTime(null);
            }
            setEndTime(timeDayjs);
        }
    };

    useEffect(() => {
        if (days.length > 0 && startTime && endTime && !errorTime) {
            setIsConfirm(false)
        } else {
            setIsConfirm(true)
        }
    }, [days, startTime, endTime, errorTime])

    const handleConfirm = () => {
        const existingConditions = Array.isArray(dateTimeConditions)
            ? dateTimeConditions
            : [];

        // Exclude the current item while editing so it won't overlap with itself.
        const conditionsForOverlapCheck = editingOuterIndex === null
            ? existingConditions
            : existingConditions.filter((_, index) => index !== editingOuterIndex);

        const oldDateTime = handleConvertDateTimeObjKey(conditionsForOverlapCheck);
        let newValue = [{
            days: days,
            start: startTime.format('HH:mm'),
            end: endTime.format('HH:mm')
        }];
        const newDateTime = handleConvertDateTimeObjKey(newValue);

        let hasOverlap = false;

        outerLoop:
        for (const day in newDateTime) {
            const oldDateTime_key_name = Object.keys(oldDateTime);
            const day_duplicate = oldDateTime_key_name.includes(day);
            if (day_duplicate && oldDateTime[day].length > 0) {
                for (const time of oldDateTime[day]) {
                    const oldStartTimeMinutes = timeStringToMinutes(time.start);
                    const oldEndTimeMinutes = timeStringToMinutes(time.end);
                    const newStartTimeMinutes = timeStringToMinutes(newDateTime[day][0].start);
                    const newEndTimeMinutes = timeStringToMinutes(newDateTime[day][0].end);

                    if (oldStartTimeMinutes < newEndTimeMinutes && oldEndTimeMinutes > newStartTimeMinutes) {
                        hasOverlap = true;
                        break outerLoop;
                    }
                }
            }
        };

        setErrorOverlap(hasOverlap);

        if (!hasOverlap) {
            let newDateTimeConditions = [];
            // let newValue = {
            //     days: days,
            //     start: startTime.format('HH:mm'),
            //     end: endTime.format('HH:mm')
            // };
            if (editingOuterIndex === null) {
                newDateTimeConditions = [...dateTimeConditions, newValue[0]];
            } else {
                newDateTimeConditions = dateTimeConditions.map((cond, i) => {
                    if (i !== editingOuterIndex) return cond;
                    return newValue[0];
                });
            }

            const env_properties_updated = {
                ...selectedNode.data.environment_properties,
                date_time_verification: {
                    ...selectedNode.data.environment_properties.date_time_verification,
                    date_time: newDateTimeConditions
                }
            }

            setNodes((prev) => prev.map((node) => {
                if (node.id === selectedNode.id) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            environment_properties: env_properties_updated
                        }
                    };
                }
                return node;
            }))

            onClose(env_properties_updated);
        }
    };

    function timeStringToMinutes(timeStr) {
        const [hhStr, mmStr] = timeStr.split(':');
        const hh = parseInt(hhStr, 10);
        const mm = parseInt(mmStr, 10);
        if (
            Number.isNaN(hh) || Number.isNaN(mm) ||
            hh < 0 || hh > 23 ||
            mm < 0 || mm > 59
        ) {
            throw new Error(`Invalid time format: ${timeStr}`);
        }
        return hh * 60 + mm;
    }

    const handleConvertDateTimeObjKey = (dateTimeVerification) => {
        const result = {};
        dateTimeVerification.forEach(item => {
            item?.days.forEach(day => {
                if (!result[day]) {
                    result[day] = [];
                }
                result[day].push({
                    start: item?.start,
                    end: item?.end
                });
            });
        });
        return result;
    };

    return (
        <Dialog
            fullWidth={true}
            maxWidth={"sm"}
            open={open}
        >
            <DialogTitle
                sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.white.main, paddingY: 1, paddingX: 2 }}
                className='flex justify-between items-center'
            >
                <Typography>{title}</Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        padding: 0,
                        color: theme.palette.white.main
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ padding: 0 }}>
                <div className='py-4 px-4'>
                    <div className='flex flex-col gap-6'>
                        <Typography variant='h6' color='primary'>Date</Typography>
                        <div className='flex felx-row gap-2'>
                            <Chip label="EveryDay" name="everyday" clickable={true} color={isEveryDay ? 'primary' : 'default'} onClick={() => handleSetDayTypes('everyday')} />
                            <Chip label="Workday" name="workday" clickable={true} color={isWorkDay ? 'primary' : 'default'} onClick={() => handleSetDayTypes('workday')} />
                            <Chip label="Weekend" name="weekend" clickable={true} color={isWeekEnd ? 'primary' : 'default'} onClick={() => handleSetDayTypes('weekend')} />
                        </div>
                        <div className='mb-4'>
                            <ToggleButtonGroup
                                color="primary"
                                size="large"
                                fullWidth
                                value={days}
                                onChange={handleSelectedDay}
                            >
                                <ToggleButton value="sunday">
                                    Sun
                                </ToggleButton>
                                <ToggleButton value="monday">
                                    Mon
                                </ToggleButton>
                                <ToggleButton value="tuesday">
                                    Tue
                                </ToggleButton>
                                <ToggleButton value="wednesday">
                                    Wed
                                </ToggleButton>
                                <ToggleButton value="thursday">
                                    Thu
                                </ToggleButton>
                                <ToggleButton value="friday">
                                    Fri
                                </ToggleButton>
                                <ToggleButton value="saturday">
                                    Sat
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </div>
                        <Divider />
                    </div>
                    <div className='flex flex-col gap-4 mt-4'>
                        <Typography variant='h6' color='primary'>Time</Typography>
                        <div className='mb-6'>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <div className='flex flex-row gap-2 justify-center items-center'>
                                    <TimePicker
                                        label="Start"
                                        ampm={false}
                                        value={startTime}
                                        maxTime={endTime}
                                        onChange={(newValue) => handleTimeChange(newValue, 'start')}
                                    />
                                    <Typography>-</Typography>
                                    <TimePicker
                                        label="End"
                                        ampm={false}
                                        value={endTime}
                                        minTime={startTime}
                                        disabled={!startTime}
                                        onChange={(newValue) => handleTimeChange(newValue, 'end')}
                                    />
                                </div>
                            </LocalizationProvider>
                        </div>
                    </div>
                    {errorOverlap && (
                        <div className='flex justify-start items-center mt-2'>
                            <Typography variant='subtitle2' color='error'>Error: Time is overlap</Typography>
                        </div>
                    )}
                </div>
            </DialogContent>
            <DialogActions>
                <div className='px-3 py-1'>
                    <Button variant='contained' disabled={isConfirm} onClick={handleConfirm}>Confirm</Button>
                </div>
            </DialogActions>
        </Dialog>
    )
}

export default DateTimeDialog